import React from 'react';
import { useDraft } from '../../draft/DraftContext';
import { Box, Grid } from '@mui/material';
import RatingOptionsHeader from '../../../../../../primeX-underwriter/components/underwriterRatingOptions/components/shared/RatingOptionsHeader';
import SectionCard from '../../shared/SectionCard';
import type {
  RateModifier,
  RateModifierCategory,
} from '../../../../api/types/rater-x-preset';
import UnderwriterModifier from './UnderwriterModifier';
import SectionRatingDetails from '../../shared/SectionRatingDetails';
import SectionHeaderLabel from '../../shared/SectionHeaderLabel';
import { Show } from '../../../../../../../../components/Show';
import ModifierCategoryToggle from './ModifierCategoryToggle';
import ReferralStrategyDropdown from '../../shared/ReferralStrategyDropdown/ReferralStrategyDropdown';
import {
  ReferralStrategyVariant,
  getUnderwriteQuoteCurrency,
  isCoverageEndorsementsState,
} from '../../types';
import { canShowModifier as canShowRateModifier } from './modifierVisibility';
import { useUnderwriterContext } from '../../provider/UnderwriterProvider';
import { useScopes } from '../../../../../../../../components/hooks/useScopes';
import { determineIfAllowedScopes } from '../../../../../../../../utils/next/auth.utils';
import {
  UW_PANEL_HEADER_MIN_HEIGHT,
  UW_PANEL_HEADER_PADDING_Y,
  UW_PANEL_HEADING_WEIGHT,
  UW_PANEL_TITLE_FONT_SIZE,
} from '../uwTileSpacing';

const UnderwriterRatingOptions = (props: UnderwriterRatingOptionsProps) => {
  const context = useUnderwriterContext();
  // Draft subtotals for rate-modifier sections, keyed by the category id.
  // Undefined with the flag off, so these render committed as they always have.
  const { rate } = useDraft();
  const draftFor = (id: string) =>
    rate.status === 'ok' ? rate.premiumByCategory[id] : undefined;
  const isMTEFlow = isCoverageEndorsementsState(context.state);

  const scopes = useScopes();
  const isPowerUser = determineIfAllowedScopes(scopes, ['superusers:manage']);

  const canShowModifier = (rateModifier: RateModifier) =>
    canShowRateModifier(rateModifier, {
      productType: context.state.productType,
      isPowerUser,
    });

  // Precompute categoryRatingOptionKeys for each modifier
  const categoryRatingOptionKeysMap = React.useMemo(() => {
    const map: Record<string, string[]> = {};
    props.rateModifiers?.forEach((modifier) => {
      map[modifier.id] =
        modifier.rateModifiers
          ?.filter(canShowModifier)
          .map((rateModifier) => rateModifier.key) ?? [];
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.rateModifiers, context.state.productType, isPowerUser]);

  return (
    props.rateModifiers?.map((modifier) => {
      const categoryRatingOptionKeys = categoryRatingOptionKeysMap[modifier.id];

      return (
        <SectionCard key={modifier.id}>
          <RatingOptionsHeader
            paddingY={UW_PANEL_HEADER_PADDING_Y}
            minHeight={UW_PANEL_HEADER_MIN_HEIGHT}
            fontSize={UW_PANEL_TITLE_FONT_SIZE}
            fontWeight={UW_PANEL_HEADING_WEIGHT}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  flexWrap: 'nowrap',
                  minWidth: 0,
                  width: '25%',
                }}
              >
                <SectionHeaderLabel
                  title={modifier.title}
                  appliedRules={modifier.appliedRules}
                  warnings={modifier.warnings}
                  referralReasons={modifier.referralReasons}
                />
                <Show when={!isMTEFlow}>
                  {categoryRatingOptionKeys.length > 0 && (
                    <ReferralStrategyDropdown
                      variant={ReferralStrategyVariant.CATEGORY_RATING_OPTION}
                      keys={categoryRatingOptionKeys}
                    />
                  )}
                </Show>
              </Box>

              <Show when={modifier.isEditable}>
                <Box mr={1}>
                  <ModifierCategoryToggle modifier={modifier} />
                </Box>
              </Show>
            </Box>
          </RatingOptionsHeader>
          <Show when={modifier.isSelected}>
            {modifier.rateModifiers
              ?.filter(canShowModifier)
              .map((rateModifier) => (
                <UnderwriterModifier
                  key={rateModifier.key}
                  {...rateModifier}
                  id={rateModifier.key}
                />
              ))}

            <SectionRatingDetails
              ratingDetails={modifier.ratingDetails}
              draftPremium={draftFor(modifier.id)?.premium}
              draftAggregatePremium={draftFor(modifier.id)?.aggregatePremium}
              expectedRatingDetails={modifier.expectedRatingDetails}
              displayModelPremium={modifier.displayModelPremium}
              currency={
                isCoverageEndorsementsState(context.state)
                  ? context.state.policy.currency
                  : getUnderwriteQuoteCurrency(context.state)
              }
            />
          </Show>
        </SectionCard>
      );
    }) ?? null
  );
};

export default UnderwriterRatingOptions;

type UnderwriterRatingOptionsProps = {
  rateModifiers: RateModifierCategory[] | undefined;
};
